import { slugify, getLocale, lang } from "../js/utils"
import langData from "../data/lang"
import config from "../data/siteConfig"

// grouped locations get an extra autosuggest value (e.g. "Budapest (összes)") that searches for the location
// and all its sub-locations (e.g. "Budapest I.")
const { GROUPED_LOCATIONS } = config()

const toGroupValue = (name, groupLabel = lang("advanced_search").group) => `${name} (${groupLabel})`

// group values of every locale mapped to their location, e.g. { "Budapest (összes)": "Budapest", "Budapest (all)": "Budapest" }
const groupValues = Object.fromEntries(
  GROUPED_LOCATIONS.flatMap((name) =>
    Object.values(langData).map((l) => [toGroupValue(name, l.advanced_search.group), name])
  )
)

// returns the location of a group value (e.g. "Budapest"), or null if the value is not a group value
export const parseGroupValue = (value) => groupValues[value] || null

// this is a cache that contains all previously entered autosuggest name / value pairs
const autocompleteData = {}
const autosuggestCache = {}

export default async (prefix, filter = false) => {
  // load data that was previously generated by 11ty SSG
  if (!autocompleteData[getLocale()]) {
    const resp = await fetch(`/autocomplete-${getLocale()}.json`, { method: "GET" })
    autocompleteData[getLocale()] = await resp.json()
  }

  // check autosuggest cache
  if (filter) {
    if (autosuggestCache[`${prefix}_${filter}`]) {
      return autosuggestCache[`${prefix}_${filter}`]
    }
  } else if (autosuggestCache[prefix]) {
    return autosuggestCache[prefix]
  }

  let filterArray
  if (filter) filterArray = filter.split(",")

  let res = []

  // filter items
  Object.keys(autocompleteData[getLocale()]).forEach(key => {
    if (!filter || filter.length === 0 || (filterArray && filterArray.indexOf(key) > -1)) {
      const items = autocompleteData[getLocale()][key].filter(value => {
        // in case if the value is a string check matches one level depper as well
        if (Array.isArray(value)) {
          if (value.find(subvalue => slugify(subvalue).indexOf(slugify(prefix)) === 0)) return true
          return false
        }

        if (slugify(value).indexOf(slugify(prefix)) === 0) return true
        return false
      })

      // flattening down the results (filtered items can be a list of strings or arrays with list of strings),
      // dropping the empty string an empty item list would leave behind
      res = res.concat(items.toString().split(",").filter(Boolean))
    }
  })

  // sort filtered result
  const resSorted = res.sort((a, b) => {
    return a.localeCompare(b, "hu", { ignorePunctuation: false })
  })

  // remove duplicates
  const filterDuplicates = keywords => keywords.filter((v, i) => keywords.indexOf(v) === i)
  const resFiltered = filterDuplicates(resSorted)
  // resFiltered.length = resFiltered.length > 10 ? limit : resFiltered.length

  // when searching cities, offer matching group values before their location (or its first sub-location)
  if (filterArray && filterArray.some((key) => key.startsWith("varos_"))) {
    GROUPED_LOCATIONS.forEach((name) => {
      const groupValue = toGroupValue(name)
      if (slugify(groupValue).indexOf(slugify(prefix)) !== 0) return

      const index = resFiltered.findIndex((r) => r === name || r.startsWith(`${name} `))
      resFiltered.splice(Math.max(index, 0), 0, groupValue)
    })
  }

  // save the filtered result to cache
  if (filter) {
    autosuggestCache[`${prefix}_${filter}`] = resFiltered
  } else {
    autosuggestCache[prefix] = resFiltered
  }

  return resFiltered
}
